/**
 * A list built one item at a time, out of a box or out of a picker.
 *
 * Three of the fields a task is written in are lists: what it asks for, what
 * it pays, and what has to be finished before it. All three are the same
 * exchange - name one thing, press the symbol beside it, and it joins the
 * list above with a way to take it back out - so all three are this.
 *
 * What travels with the form is one hidden field per item, named by
 * `data-picker-name`. The endpoint therefore reads a plain list whether it
 * was built here or typed into a request by somebody else, and nothing about
 * it depends on the script having run.
 *
 * Without the script a free-text list is one box which carries one line, and
 * a picker is a plain select which carries one value. Both are what the form
 * did before it could hold more than one of anything, which is a worse form
 * rather than a broken one.
 */

import { query, queryAll } from "../core/dom";
import { text } from "../core/i18n";

/** How many items one list may hold when nothing else is said. */
const CEILING = 12;

/** Return the items already in `list`, by the value each stands for. */
function heldBy(list: HTMLElement): string[] {
    return queryAll<HTMLElement>("[data-picker-item]", list).map((item) => item.dataset["pickerItem"] ?? "");
}

/** Build the chip standing for one item of the list. */
function chipFor(value: string, label: string, name: string, drop: string): HTMLElement {
    const chip = document.createElement("span");
    chip.className = "chip-list__item";
    chip.dataset["pickerItem"] = value;

    const carried = document.createElement("input");
    carried.type = "hidden";
    carried.name = name;
    carried.value = value;

    const wording = document.createElement("span");
    wording.textContent = label;

    const button = document.createElement("button");
    button.type = "button";
    button.className = "chip-list__drop";
    button.dataset["pickerDrop"] = "";
    button.setAttribute("aria-label", drop);
    button.innerHTML =
        '<svg class="icon" width="12" height="12" aria-hidden="true">' +
        '<use href="/static/assets/icons/icons.svg#close"></use></svg>';

    chip.append(carried, wording, button);

    return chip;
}

/** Wire one list. */
export function bindPicker(picker: HTMLElement): void {
    const list = query<HTMLElement>("[data-picker-list]", picker);
    const name = picker.dataset["pickerName"];

    if (!list || !name) {
        return;
    }

    const box = query<HTMLInputElement>("[data-picker-input]", picker);
    const add = query<HTMLElement>("[data-picker-add]", picker);
    const source = query<HTMLSelectElement>("[data-picker-source]", picker);
    const ceiling = Number(picker.dataset["pickerMax"] ?? CEILING) || CEILING;
    const drop = text("js.picker.drop", "Odobrať zo zoznamu");

    /**
     * Say out loud that the form now holds something else.
     *
     * An item is a hidden field appearing or disappearing, which fires no
     * event of its own - so the guard which keeps the save button in step
     * with the form never heard about it, and a window whose only change was
     * a line added or taken away could not be saved at all.
     */
    const announce = (): void => {
        picker.dispatchEvent(new Event("change", { bubbles: true }));
    };

    // The box and the picker stop answering for themselves the moment the
    // list can hold more than one thing; from here on the chips are what is
    // sent.
    box?.removeAttribute("name");
    source?.removeAttribute("name");

    /** Put an option back among the choices, or take it away. */
    const offer = (value: string, available: boolean): void => {
        for (const option of Array.from(source?.options ?? [])) {
            if (option.value === value) {
                option.hidden = !available;
                option.disabled = !available;
            }
        }
    };

    /** Say whether there is still room, and stop the box when there is not. */
    const settle = (): void => {
        const full = heldBy(list).length >= ceiling;

        if (box) {
            box.disabled = full;
        }

        if (source) {
            source.disabled = full;
        }

        if (add instanceof HTMLButtonElement) {
            add.disabled = full;
        }
    };

    const put = (value: string, label: string): void => {
        const trimmed = value.trim();

        if (!trimmed || heldBy(list).includes(trimmed) || heldBy(list).length >= ceiling) {
            return;
        }

        list.append(chipFor(trimmed, label.trim() || trimmed, name, drop));
        offer(trimmed, false);
        settle();
        announce();
    };

    if (box) {
        const take = (): void => {
            put(box.value, box.value);
            box.value = "";
            box.focus();
        };

        add?.addEventListener("click", take);

        // Enter finishes the line rather than sending the whole window, which
        // is what a form with one text field in it does by default.
        box.addEventListener("keydown", (event: KeyboardEvent) => {
            if (event.key === "Enter") {
                event.preventDefault();
                take();
            }
        });
    }

    if (source) {
        // The list drawn over the control follows the value it holds, and only
        // if it is told the value moved - which is this same event, so the
        // handler has to know when it is hearing itself.
        let quiet = false;

        source.addEventListener("change", () => {
            if (quiet) {
                return;
            }

            const option = source.selectedOptions[0];
            put(source.value, option?.textContent ?? source.value);

            // Back to the prompt, so the same thing is never the answer twice
            // and the closed control keeps saying what the field is for.
            source.selectedIndex = 0;
            quiet = true;
            source.dispatchEvent(new Event("change", { bubbles: true }));
            quiet = false;
        });
    }

    list.addEventListener("click", (event: MouseEvent) => {
        const button = (event.target as HTMLElement | null)?.closest("[data-picker-drop]");
        const chip = button?.closest<HTMLElement>("[data-picker-item]");

        if (!chip) {
            return;
        }

        offer(chip.dataset["pickerItem"] ?? "", true);
        chip.remove();
        settle();
        announce();
    });

    for (const value of heldBy(list)) {
        offer(value, false);
    }

    settle();
}

/** Wire every list of the current page. */
export function initChipPickers(): void {
    for (const picker of queryAll<HTMLElement>("[data-picker]")) {
        bindPicker(picker);
    }
}
